import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { setGlobalLogoutHandler } from "@/lib/auth-utils";
import { useEffect, useCallback } from "react";
import type { AuthenticatedUser } from "@/types/auth";

async function fetchUser(): Promise<AuthenticatedUser | null> {
  try {
    // fetch is intercepted globally, so 401s are handled automatically by the interceptor
    const response = await fetch("/api/auth/user", {
      credentials: "include",
    });

    if (response.status === 401) {
      // Interceptor already handled logout, just clear cached data
      localStorage.removeItem("user");
      return null;
    }

    if (!response.ok) {
      throw new Error(`${response.status}: ${response.statusText}`);
    }

    const user = await response.json();
    // Store non-sensitive user info in localStorage for quick access
    localStorage.setItem("user", JSON.stringify(user));
    return user;
  } catch (error) {
    // Interceptor handles 401s, so we just clear local data on any error
    localStorage.removeItem("user");
    return null;
  }
}

export function useAuth() {
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  
  const { data: user, isLoading, refetch } = useQuery<AuthenticatedUser | null>({
    queryKey: ["/api/auth/user"],
    queryFn: fetchUser,
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
    // Initialize from localStorage if available
    initialData: () => {
      const cached = localStorage.getItem("user");
      return cached ? (JSON.parse(cached) as AuthenticatedUser) : undefined;
    },
  });

  const logout = useCallback(async () => {
    // Always attempt server-side logout so the httpOnly JWT cookie is cleared.
    // Without this, refreshing will "log you back in" because the cookie is still valid.
    try {
      await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    } catch {
      // Ignore network errors; still clear local state.
    }

    localStorage.removeItem("user");
    queryClient.setQueryData(["/api/auth/user"], null);
    // Optional: also cancel any in-flight queries that might refetch the user.
    queryClient.cancelQueries();

    setLocation("/login");
  }, [queryClient, setLocation]);

  // Register the logout handler globally so it can be called from interceptors
  useEffect(() => {
    setGlobalLogoutHandler(logout);
    return () => {
      setGlobalLogoutHandler(() => Promise.resolve());
    };
  }, [logout]);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    logout,
    refetch,
  };
}
